import { hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismLoadingBar,
  PrismMetricBlock,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import GeneralDemandLevel from 'lumen/components/general/demand-level';
import HotelMarketInsightDemandLevelIndicator from 'lumen/components/hotel/market-insight/demand-level-indicator';
import highlightElement from 'lumen/modifiers/highlight-element';
import { OD_API_DATA_KEYS } from 'lumen/utils/constants/overview-dashboard-constants';
import generateMarketInsightRedirectUrlQueryParams from 'lumen/utils/overview-dashboard/generate-market-insight-redirect-url-query-params';

interface HotelOverviewDashboardTilesMarketTrendsDemandLevelSignature {
  Args: {
    data: unknown;
    isLoading: unknown;
    selectedRange: unknown;
    tileId: unknown;
  };
}

export default class HotelOverviewDashboardTilesMarketTrendsDemandLevel extends Component<HotelOverviewDashboardTilesMarketTrendsDemandLevelSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare odSelections: Services['od-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandChange() {
    if (
      !this.refDateDemandLevelValue ||
      this.demandLevelValue === this.refDateDemandLevelValue
    ) {
      return '';
    }

    return this.demandLevelValue > this.refDateDemandLevelValue
      ? 'increase'
      : 'decrease';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.data.getDataValueFromKey(
      this.odSelections,
      OD_API_DATA_KEYS.mi_demand_level,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandReferenceTranslationString() {
    if (
      !this.refDateDemandLevelValue ||
      this.demandLevelValue === this.refDateDemandLevelValue
    ) {
      return this.intl.t('overview_dashboard.kpi.no_change');
    }

    const previousDemandTranslation = this.intl.t(
      `market_insight.rate_level.level${this.refDateDemandLevelValue}`,
    );

    return this.intl.t(
      `overview_dashboard.kpi.demand.reference.${this.demandChange}d`,
      {
        previousDemand: previousDemandTranslation,
      },
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasMarketInsightData() {
    // @ts-expect-error: Incorrect type
    return !this.args.data.isEmptyResponse;
  }

  // The redirect link redirects the user to Market Insight (overview or day detail view - depending on the selected date range)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiRedirectUrlQueryParams() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const refDaysAgo = this.odSelections.selectedRefDaysAgo.id;

    // @ts-expect-error: Incorrect type
    if (this.odSelections.isDayDrilldownActive) {
      const activeDrilldownDayMoment = dayjs(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.odSelections.activeDrilldownDayAsString,
      );

      return generateMarketInsightRedirectUrlQueryParams(
        // @ts-expect-error: Incorrect type
        this.args.selectedRange.start,
        // @ts-expect-error: Incorrect type
        this.args.selectedRange.end,
        refDaysAgo,
        // @ts-expect-error: Incorrect type
        activeDrilldownDayMoment,
      );
    }

    return generateMarketInsightRedirectUrlQueryParams(
      // @ts-expect-error: Incorrect type
      this.args.selectedRange.start,
      // @ts-expect-error: Incorrect type
      this.args.selectedRange.end,
      refDaysAgo,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refDateDemandLevelValue() {
    // @ts-expect-error: Incorrect type
    if (!this.odSelections.isDayDrilldownActive) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
      return this.args.data.getDataValueFromKey(
        this.odSelections,
        OD_API_DATA_KEYS.mi_demand_level,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.odSelections.selectedRefDaysAgo.id,
      );
    }

    // mi_demand_level.diff is passed as <previous_demand>|<current_demand>
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const demandDiff = this.args.data.getDataValueFromKey(
      this.odSelections,
      OD_API_DATA_KEYS.mi_demand_level_diff,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.odSelections.selectedRefDaysAgo.id,
    );

    if (demandDiff === undefined || demandDiff === null) {
      return NaN;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return Number(demandDiff.split('|')[0]);
  }

  <template>
    {{! @glint-nocheck }}
    <PrismCard
      class="prism-card--od-kpi prism-card--od-kpi--demand"
      {{highlightElement id=@tileId isLoading=@isLoading}}
    >
      <:header as |Header|>
        <Header.title>
          {{t
            (if
              (or
                this.odSelections.isDayDrilldownActive
                this.odSelections.isSingleDaySelectedInTheDatepicker
              )
              "market_insight.demand"
              "overview_dashboard.kpi.header.avg_demand"
            )
          }}
        </Header.title>
      </:header>
      <:content>
        <div class="od-kpi-content flex flex-col">
          {{#if @isLoading}}
            <PrismLoadingBar />
          {{else}}
            {{#if this.hasMarketInsightData}}
              {{#if this.demandLevelValue}}
                <PrismMetricBlock @align="left">
                  <:value><div class="text-400--bold">
                      <GeneralDemandLevel
                        @demandChange={{this.demandChange}}
                        @demandLevel={{this.demandLevelValue}}
                        @size="normal"
                      />
                    </div></:value>
                  <:label>
                    {{t
                      "general.daysago"
                      count=this.odSelections.selectedRefDaysAgo.id
                    }}
                  </:label>
                </PrismMetricBlock>
                <PrismMetricBlock @align="left">
                  <:value>
                    <div class="demand-level-indicator-container">
                      <HotelMarketInsightDemandLevelIndicator
                        @params={{hash
                          demandLevelColorScaleNumber=this.demandLevelValue
                          refDateDemandLevelColorScaleNumber=this.refDateDemandLevelValue
                        }}
                      />
                    </div>
                  </:value>
                  <:label>
                    {{this.demandReferenceTranslationString}}
                  </:label>
                </PrismMetricBlock>
              {{else}}
                <PrismMetricBlock @align="left">
                  <:value>
                    {{t "general.missing"}}
                  </:value>
                  <:label>
                    {{t
                      "general.daysago"
                      count=this.odSelections.selectedRefDaysAgo.id
                    }}
                  </:label>
                </PrismMetricBlock>
              {{/if}}

              <div class="redirect-link-container">
                <LinkTo
                  @model={{this.hotelAndMarketService.hotel.id}}
                  @query={{this.kpiRedirectUrlQueryParams}}
                  @route="hotel.market-insight"
                  class="prism-link prism-link--standalone"
                  data-event-id="hotel_overview-kpi_main_redirect-click"
                  data-event-value={{@tileId}}
                  target="_blank"
                >
                  {{t "overview_dashboard.kpi.link.market_insight"}}
                </LinkTo>
              </div>
            {{else}}
              <PrismMetricBlock @align="left">
                <:value>
                  {{t "general.missing"}}
                </:value>
                <:label>
                  {{t "overview_dashboard.kpi.range_selection"}}
                </:label>
              </PrismMetricBlock>
            {{/if}}
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
